import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ALL_RISK_TIER_NUMBERS, TIER_CATALOG, RiskTierNumber } from '../../tiers/tierCatalog';
import { AccountTierContextResponse, TierContextRow } from '../../types/tierContext';
import { fmtUsd } from './format';
import TierPerformanceOverTimeChart from './TierPerformanceOverTimeChart';

export type TierFilter = 'all' | RiskTierNumber;

interface Props {
  context: AccountTierContextResponse | null;
  filter: TierFilter;
  onFilterChange: (filter: TierFilter) => void;
  compact?: boolean;
  accountId?: string;
  showInlineCharts?: boolean;
}

const TierSpectrumRail: React.FC<Props> = ({
  context,
  filter,
  onFilterChange,
  compact = false,
  accountId,
  showInlineCharts = false,
}) => {
  const rows: TierContextRow[] =
    context?.tiers ??
    ALL_RISK_TIER_NUMBERS.map((riskTier) => ({
      riskTier,
      catalog: TIER_CATALOG[riskTier],
      slot: null,
      enabled: false,
      budget: null,
      positionCounts: { active: 0, pending: 0, recommendations: 0 },
      capabilities: {
        macroPortfolio: riskTier === 1,
        thesisOrchestrator: riskTier === 6,
        configurable: riskTier === 1 || riskTier === 6,
      },
    }));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onFilterChange('all')}
          className={clsx(
            'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
            filter === 'all'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-300 hover:text-white',
          )}
        >
          All tiers
        </button>
        {context ? (
          <div className="text-right text-xs text-slate-300">
            <div>NAV {fmtUsd(context.accountNavUsd)}</div>
            <div>Deployable {fmtUsd(context.deployableUsd)}</div>
          </div>
        ) : null}
      </div>

      <div
        className={clsx(
          'grid gap-1',
          compact ? 'grid-cols-3 sm:grid-cols-6' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
        )}
      >
        {rows.map((row) => {
          const catalog = TIER_CATALOG[row.riskTier];
          const isActive = filter === row.riskTier;
          const deployed = row.budget?.tierDeployedUsd ?? 0;
          const budget = row.budget?.tierBudgetUsd ?? 0;
          const utilPct = budget > 0 ? Math.min(100, (deployed / budget) * 100) : 0;
          const totalPositions =
            row.positionCounts.active +
            row.positionCounts.pending +
            row.positionCounts.recommendations;
          const deepLink = accountId
            ? `/app/accounts/${accountId}?tier=${row.riskTier}`
            : undefined;

          const inner = (
            <>
              <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  {catalog.shortName}
                </span>
                {row.enabled ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" title="Enabled" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-600" title="Inactive" />
                )}
              </div>
              <p className="mt-1 line-clamp-2 text-xs font-medium text-white">
                {catalog.name.split('/')[0].trim()}
              </p>
              {!compact && budget > 0 ? (
                <p className="mt-1 text-[10px] text-slate-400">
                  {fmtUsd(deployed)} / {fmtUsd(budget)}
                </p>
              ) : null}
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-700">
                <div
                  className={clsx('h-full', catalog.railClass)}
                  style={{ width: `${utilPct}%` }}
                />
              </div>
              {totalPositions > 0 ? (
                <p className="mt-1 text-[10px] text-slate-400">{totalPositions} positions</p>
              ) : null}
            </>
          );

          const className = clsx(
            'rounded-lg border p-2 text-left transition-all',
            isActive
              ? 'border-white/30 bg-slate-800/80 ring-1 ring-white/20'
              : 'border-slate-700/80 bg-slate-900/40 hover:border-slate-500',
            !row.enabled && 'opacity-60',
          );

          if (deepLink && !compact) {
            return (
              <Link
                key={row.riskTier}
                to={deepLink}
                className={className}
                onClick={() => onFilterChange(row.riskTier)}
              >
                {inner}
              </Link>
            );
          }

          return (
            <button
              key={row.riskTier}
              type="button"
              onClick={() => onFilterChange(row.riskTier)}
              className={className}
            >
              {inner}
            </button>
          );
        })}
      </div>
      {showInlineCharts && accountId && filter !== 'all' ? (
        <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/50 p-2">
          <TierPerformanceOverTimeChart
            accountId={accountId}
            riskTier={filter}
            compact
          />
        </div>
      ) : null}
    </div>
  );
};

export default TierSpectrumRail;
